import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Tabs } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import {
  getBasketPositionsAction,
  getBasketOrdersAction,
  getBasketTradesAction,
  getBasketLogsAction,
  getStrategyPositionsAction,
  getStrategyOrdersAction,
  getStrategyTradesAction,
  getStrategyLogsAction
} from '../../../../redux/actions/brokerAction';
import { getTicksData } from '../../../../redux/actions/optionChain';
import { calculatePositionRealisedPnl, calculatePositionUnrealisedPnl } from '../../../../util/helper';
import LegSLTPModal from '../../positionPage/position-comp/LegSLTPModal';

// Imported Components
import PositionsTable from './PositionsTable';
import OrdersTable from './OrdersTable';
import TradesTable from './TradesTable';
import LogsDisplay from './LogsDisplay';
import { useToast } from 'components/common/custom-toast/Toast';

const { TabPane } = Tabs;

const StrategyDataTabs = ({
  strategyId,
  user,
  socketJWT,
  source,
  isBasketMode = false,
  strategies = []
}) => {
  const [activeTab, setActiveTab] = useState('positions');
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [trades, setTrades] = useState([]);
  const [logs, setLogs] = useState([]);
  const [baskets, setBaskets] = useState([]);
  const [loading, setLoading] = useState(false);
  const subscriptionRef = useRef(null);
  const positionsRef = useRef([]);
  const subscriptionMap = useRef({});
  const sockSource = useSelector((state) => state.sockSource?.source);
  const brokersList = useSelector((state) => state?.brokers?.list || []);
  const loggedInBrokers = useMemo(() => {
    return brokersList.filter(broker => broker?.status === 'loggedIn');
  }, [brokersList]);
  const dispatch = useDispatch();
  const [isLegModalOpen, setIsLegModalOpen] = useState(false);
  const [modalLegRow, setModalLegRow] = useState(null);
  const toast = useToast()


  useEffect(() => {
    if (strategyId) {
      loadInitialData();
    }
  }, [strategyId, isBasketMode]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      const positionsData = await dispatch(
        isBasketMode
          ? getBasketPositionsAction(strategyId)
          : getStrategyPositionsAction(strategyId)
      );
      if (positionsData && !positionsData.sessionExpired) {
        const seeded = (positionsData || []).map((p) => ({
          ...p,
          averagePrice: Number(p.averagePrice || 0),
        }));
        const tokens = seeded.map((p) => p.instrumentToken).filter(Boolean);
        let ticks = {};
        if (tokens.length > 0) {
          try {
            ticks = await getTicksData(tokens);
          } catch (e) {
            ticks = {};
          }
        }
        const withPnL = seeded.map((p) => {
          const lp = Number(ticks?.[p.instrumentToken]?.lp || 0);
          const realized = Number(calculatePositionRealisedPnl(p));
          const unrealized = Number(calculatePositionUnrealisedPnl(p, lp));
          const qty = Number(p.netQuantity || 0);
          const iv = Number(ticks?.[p.instrumentToken]?.iv || 0);
          const delta = Number(ticks?.[p.instrumentToken]?.d || 0) * qty;
          const theta = Number(ticks?.[p.instrumentToken]?.t || 0) * qty;
          const vega = Number(ticks?.[p.instrumentToken]?.ve || 0) * qty;
          const gamma = Number(ticks?.[p.instrumentToken]?.g || 0) * qty;

          return {
            ...p,
            ltp: lp,
            realizedPNL: realized,
            unrealizedPNL: unrealized,
            totalPNL: Number(realized) + Number(unrealized),
            iv: iv,
            delta: delta,
            theta: theta,
            vega: vega,
            gamma: gamma
          };
        });

        const sortedWithPnL = [...withPnL].sort((a, b) => {
          if (a.netQuantity !== 0 && b.netQuantity === 0) return -1;
          if (a.netQuantity === 0 && b.netQuantity !== 0) return 1;

          const nameA = a.instrument?.name || '';
          const nameB = b.instrument?.name || '';
          if (nameA !== nameB) return nameA.localeCompare(nameB);

          const expiryA = a.instrument?.expiry || '';
          const expiryB = b.instrument?.expiry || '';
          if (expiryA !== expiryB) return expiryA.localeCompare(expiryB);

          const strikeA = a.instrument?.strikePrice || 0;
          const strikeB = b.instrument?.strikePrice || 0;
          if (strikeA !== strikeB) return strikeA - strikeB;

          const typeA = a.instrument?.instrumentType || '';
          const typeB = b.instrument?.instrumentType || '';
          return typeA.localeCompare(typeB);
        });

        positionsRef.current = sortedWithPnL;
        setPositions(sortedWithPnL);
        subscribeToSockSource(sortedWithPnL);
      }

      const ordersData = await dispatch(
        isBasketMode
          ? getBasketOrdersAction(strategyId)
          : getStrategyOrdersAction(strategyId)
      );
      if (ordersData && !ordersData.sessionExpired) {
        setOrders(ordersData);
      }

      const tradesData = await dispatch(
        isBasketMode
          ? getBasketTradesAction(strategyId)
          : getStrategyTradesAction(strategyId)
      );
      if (tradesData && !tradesData.sessionExpired) {
        setTrades(tradesData);
      }

      const logsData = await dispatch(
        isBasketMode
          ? getBasketLogsAction(strategyId)
          : getStrategyLogsAction(strategyId)
      );
      if (logsData && !logsData.sessionExpired) {
        setLogs(logsData);
      }
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error(`Failed to load ${isBasketMode ? 'basket' : 'strategy'} data`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenLegModal = (rowData) => {
    setModalLegRow(rowData);
    setIsLegModalOpen(true);
  };

  const removeOrder = (orderId) => {
    setOrders((prev) => prev.filter((o) => o.id !== orderId));
  };

  const removePosition = (positionId) => {
    setPositions((prev) => {
      const next = prev.filter((p) => p.id !== positionId);
      positionsRef.current = next;
      return next;
    });
  };

  const removeTrade = (tradeId) => {
    setTrades((prev) => prev.filter((t) => t.id !== tradeId));
  };

  useEffect(() => {
    if (!source || !socketJWT || loggedInBrokers.length === 0) return;

    const subscriptions = [];

    loggedInBrokers.forEach(broker => {
      const channelName = `${socketJWT.wsId}-${broker.id}`;
      console.log('Subscribing to channel:', channelName);

      const subscription = source
        .subscribe(channelName, (message) => {
          const payload = message?.data || message;
          const channel = payload?.channel;

          if (!channel) return;

          if (channel === 'algo_orders' && (payload?.order || payload?.data)) {
            const order = payload?.order || payload?.data;
            if (isBasketMode) {
              if (order.basketId !== strategyId) {
                return;
              }
            } else {
              if (order.strategyId !== strategyId) {
                return;
              }
            }

            if (payload.deleted) {
              removeOrder(order.id);
              return;
            }

            setOrders((prevOrders) => {
              const idx = prevOrders.findIndex((o) => o.id === order.id);
              if (idx >= 0) {
                const next = [...prevOrders];
                next[idx] = order;
                return next;
              }
              return [order, ...prevOrders];
            });
          }

          if (channel === 'algo_positions' && (payload?.position || payload?.data)) {
            const position = payload?.position || payload?.data;
            if (isBasketMode) {
              if (position.basketId !== strategyId) {
                return;
              }
            } else {
              if (position.strategyId !== strategyId) {
                return;
              }
            }

            if (payload.deleted) {
              removePosition(position.id);
              return;
            }

            setPositions((prevPositions) => {
              const next = [...prevPositions];
              const idx = next.findIndex((p) => p.id === position.id);
              const merged = idx >= 0 ? { ...next[idx], ...position } : position;
              const lp = Number(merged.ltp || 0);
              const realized = Number(calculatePositionRealisedPnl(merged));
              const unrealized = Number(calculatePositionUnrealisedPnl(merged, lp));
              const updated = {
                ...merged,
                ltp: lp,
                realizedPNL: realized,
                unrealizedPNL: unrealized,
                totalPNL: realized + unrealized,
              };
              if (idx >= 0) {
                next[idx] = updated;
              } else {
                next.push(updated);
                subscribeSockToken(updated);
              }
              positionsRef.current = next;
              return next;
            });
          }

          if (channel === 'algo_trades' && (payload?.trade || payload?.data)) {
            const trade = payload?.trade || payload?.data;
            if (isBasketMode) {
              if (trade.basketId !== strategyId) {
                return;
              }
            } else {
              if (trade.strategyId !== strategyId) {
                return;
              }
            }

            if (payload.deleted) {
              removeTrade(trade.id);
              return;
            }

            setTrades((prevTrades) => [trade, ...prevTrades]);
          }

          if (channel === 'algo_logs' && (payload?.log || payload?.data)) {
            const log = payload?.log || payload?.data;
            if (isBasketMode) {
              if (log.basketId !== strategyId) {
                return;
              }
            } else {
              if (log.strategyId !== strategyId) {
                return;
              }
            }
            setLogs((prevLogs) => [log, ...prevLogs]);

            if (log?.silent !== true) {
              const msg = log?.msg?.trim();
              if (!msg) return;

              switch (log?.color) {
                case 'red':
                  toast.error(msg);
                  break;
                case 'yellow':
                  toast.warning(msg);
                  break;
                case 'gray':
                  toast.info(msg);
                  break;
                case 'green':
                default:
                  toast.success(msg);
              }
            }
          }

          if (channel === 'algo_baskets' && (payload?.basket || payload?.data)) {
            const basket = payload?.basket || payload?.data;
            setBaskets((prevBaskets) => {
              const idx = prevBaskets.findIndex((b) => b.id === basket.id);
              if (idx >= 0) {
                const next = [...prevBaskets];
                next[idx] = basket;
                return next;
              }
              return [...prevBaskets, basket];
            });
          }
        })
        .on('subscribe', () => {
        })
        .on('unsubscribe', () => {
        })
        .on('error', (error) => {
          console.error(`Error in strategy channel ${channelName}:`, error);
        });

      subscriptions.push(subscription);
    });

    subscriptionRef.current = subscriptions;

    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.forEach(subscription => {
          if (subscription) {
            subscription.unsubscribe();
          }
        });
        subscriptionRef.current = null;
      }
    };
  }, [source, socketJWT, loggedInBrokers]);

  useEffect(() => {
    return () => {
      unsubscribeSockSource();
    };
  }, []);

  const subscribeToSockSource = (positions) => {
    if (!sockSource || !positions || positions.length === 0) return;

    unsubscribeSockSource();

    subscriptionMap.current = {};
    positions.forEach((position, index) => {
      if (position.instrumentToken) {
        const subscription = sockSource
          .subscribe(position.instrumentToken + "", (message) => {
            handleResponse(
              message,
              index,
              position.instrumentToken,
              position.id
            );
          })
          .on("unsubscribe", () => { })
          .on("subscribe", () => { })
          .on("error", (error) => {
            console.error(`Error subscribing to token ${position.instrumentToken}:`, error);
          });
        subscriptionMap.current[position.instrumentToken] = subscription;
      }
    });
  };

  const unsubscribeSockSource = () => {
    const subs = subscriptionMap.current || {};
    Object.keys(subs).forEach((token) => {
      try { subs[token]?.unsubscribe(); } catch { }
    });
    subscriptionMap.current = {};
  };

  const subscribeSockToken = (position) => {
    if (!sockSource || !position || !position.instrumentToken) return;
    const tokenKey = position.instrumentToken + "";
    if (subscriptionMap.current[tokenKey]) return;
    const subscription = sockSource
      .subscribe(tokenKey, (message) => {
        handleResponse(
          message,
          -1,
          position.instrumentToken,
          position.id
        );
      })
      .on("unsubscribe", () => { })
      .on("subscribe", () => { })
      .on("error", (error) => {
        console.error(`Error subscribing to token ${position.instrumentToken}:`, error);
      });
    subscriptionMap.current[position.instrumentToken] = subscription;
  };

  const handleResponse = (message, index, token, positionId) => {
    const data = message?.data || {};
    const lp = Number(data?.lp || 0);
    const deltaRaw = data?.d != null ? Number(data?.d) : undefined;
    const gammaRaw = data?.g != null ? Number(data?.g) : undefined;
    const thetaRaw = data?.t != null ? Number(data?.t) : undefined;
    const vegaRaw = data?.ve != null ? Number(data?.ve) : undefined;
    const iv = data?.iv != null ? Number(data?.iv) : undefined;
    if (!lp) return;


    setPositions((prev) => {
      const next = prev.map((p) => {
        if (p.instrumentToken === token) {
          const realized = Number(calculatePositionRealisedPnl(p));
          const unrealized = Number(calculatePositionUnrealisedPnl(p, lp));
          const totalPNL = realized + unrealized;
          const qty = Number(p.netQuantity || 0);

          return {
            ...p,
            ltp: lp,
            delta: deltaRaw !== undefined ? deltaRaw * qty : p.delta,
            gamma: gammaRaw !== undefined ? gammaRaw * qty : p.gamma,
            theta: thetaRaw !== undefined ? thetaRaw * qty : p.theta,
            vega: vegaRaw !== undefined ? vegaRaw * qty : p.vega,
            iv: iv !== undefined ? iv : p.iv,
            realizedPNL: realized,
            unrealizedPNL: unrealized,
            totalPNL: totalPNL,
          };
        }
        return p;
      });
      positionsRef.current = next;
      return next;
    });
  };


  return (
    <div style={{
      padding: 12,
      borderRadius: 8,
      width: '100%',
      maxWidth: '100%',
      overflow: 'hidden'
    }}>

      <div style={{ width: '100%', overflow: 'hidden' }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Positions" key="positions">
            <PositionsTable positions={positions} loading={loading} onOpenLegModal={handleOpenLegModal} strategies={strategies} />
          </TabPane>
          <TabPane tab="Orders" key="orders">
            <OrdersTable orders={orders} loading={loading} />
          </TabPane>
          <TabPane tab="Trades" key="trades">
            <TradesTable trades={trades} loading={loading} />
          </TabPane>
          <TabPane tab="Logs" key="logs">
            <LogsDisplay logs={logs} loading={loading} />
          </TabPane>
        </Tabs>
      </div>

      {isLegModalOpen && (
        <LegSLTPModal
          show={isLegModalOpen}
          setShow={setIsLegModalOpen}
          rowData={modalLegRow}
        />
      )}
    </div>
  );
};

export default StrategyDataTabs;
